import { parseYYYYMMDD } from '@webapp/core/utils/dates/parseYYYYMMDD'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  type GlobalSearchResultTypeEnum,
  GlobalSearchResultTypes,
} from '../../../../../../value-objects/enums'

const MS_PER_DAY = 24 * 60 * 60 * 1000

const isDateOnly = (date: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(date)

const parseDate = (date: ISODateString): Date =>
  isDateOnly(date) ? parseYYYYMMDD(date) : new Date(date)

const calendarDaysDiff = (target: Date): number => {
  const targetDay = new Date(target)
  const now = new Date()

  targetDay.setHours(0, 0, 0, 0)
  now.setHours(0, 0, 0, 0)

  return Math.round((targetDay.getTime() - now.getTime()) / MS_PER_DAY)
}

export const useFormatGlobalSearchDateLabel = () => {
  const intl = useProcurifyIntl()

  const formatDate = (date: ISODateString, allowFuture = false): string => {
    const parsed = parseDate(date)
    const daysDiff = calendarDaysDiff(parsed)
    const upperBound = allowFuture ? 1 : 0

    if (daysDiff >= -1 && daysDiff <= upperBound) {
      return intl.formatRelativeTime(daysDiff, 'day', { numeric: 'auto' })
    }

    return intl.formatDate(parsed, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    type: GlobalSearchResultTypeEnum,
    date: ISODateString
  ): Nullable<string> => {
    switch (type) {
      case GlobalSearchResultTypes.ORDER_REQUEST:
      case GlobalSearchResultTypes.EXPENSE_REQUEST:
      case GlobalSearchResultTypes.TRAVEL_REQUEST:
      case GlobalSearchResultTypes.PURCHASE_ORDER:
        return intl.formatMessage(messages.dateCreated, {
          date: formatDate(date),
        })
      case GlobalSearchResultTypes.BILL:
        return intl.formatMessage(messages.dateDue, {
          date: formatDate(date, true),
        })
      case GlobalSearchResultTypes.PAYMENT:
        return intl.formatMessage(messages.dateScheduled, {
          date: formatDate(date, true),
        })
      case GlobalSearchResultTypes.CONTRACT:
        return intl.formatMessage(messages.dateEnds, {
          date: intl.formatDate(parseDate(date), {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
        })
      case GlobalSearchResultTypes.CATALOG_ITEM:
      case GlobalSearchResultTypes.VENDOR:
      default:
        return null
    }
  }
}
